// types/KanbanTask.ts
import dayjs, { Dayjs } from 'dayjs';
// stores/KanbanTaskStore.ts
import { makeAutoObservable, observable, runInAction } from "mobx";
import type { ObservableMap } from "mobx";
import { message } from "antd";
import ApiStore from "./ApiStore";
import { Attachment, ScheduledAndPriorityUpdate, TaskTimeLog, TaskTimeMetrics } from '../api/KanbanTaskApiClient';
import { Employee } from '../models/Employee';
import i18n from '../i18n';
import { richTextToPlain } from '../components/editor/richText';
import { isVisibleOnBoard } from './kanban/boardRules';
import { isInlineImagesNotAllowedError } from './kanban/TaskStore';
import type { TaskStore } from './kanban/TaskStore';
import { isStatusNotEmptyError } from './kanban/statusErrors';
import { routesForMode } from '../api/kanban/kanbanRoutes';
import type { KanbanMode } from '../api/kanban/kanbanRoutes';

export type KanbanTaskStatus = {
  id: string;
  name: string;
  index: number;
  projectId: string;
  isFinalStatus: boolean;
};

export type KanbanTask = {
  id: string;
  publicId?: string;
  title: string;
  description: string;
  summary: string;
  statusId: string;
  priority: number;
  index: number;
  projectId: string;
  assigneeId?: string;
  createdByUserId: string;
  parentTaskId?: string;
  createdDate: Dayjs;
  updatedDate: Dayjs;
  startDate?: Dayjs;
  dueDate?: Dayjs;
  estimatedTime?: string;
  /** Soft-delete marker from the API. Deleted tasks are served by `getDeletedTasks` instead. */
  isDeleted?: boolean;
  modifiedDate: Dayjs; // Contains the date of the last task modification (includ action with task and comments for it)
  assignee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoId?: string;
  } | Employee;
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoId?: string;
  };
  status?: KanbanTaskStatus;
  comments?: KanbanTaskComment[];
  subTasks?: KanbanTask[];
}

export type KanbanTaskComment = {
  id: string;
  content: string;
  taskId: string;
  authorId: string;
  createdDate: Dayjs;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    photoId?: string;
  };
}

export type NewKanbanTask = {
  title: string;
  description: string;
  projectId: number;
  statusId: number;
  assigneeId?: number;
  parentTaskId?: number;
  priority: number;
  /** Optional: the server sets the author from the caller (JWT user, or the link's author on the public board). */
  createdByUserId?: number;
  startDate?: string;
  dueDate?: string;
  summary?: string;
}

export type NewKanbanTaskComment = {
  content: string;
  taskId: string;
  authorId?: number;
}

export type ExportTimeLogsParams = {
  userId: string;
  projectId: string;
  startPeriod?: string;
  endPeriod?: string;
  exportAllLogs?: boolean;
  language?: string;
}

export type KanbanTaskChange = {
  id: string;
  propertyName?: string;
  previousValue?: string;
  newValue?: string;
  taskId: string;
  createdDate: Dayjs;
};

/** `PublicProjectDto` of the board token's project (server Task 7); read by the public board only. */
export type PublicProject = {
  id: number;
  name: string;
  slug?: string;
  companyId?: number;
};

class KanbanTaskStore {
  ApiStore: ApiStore;
  /** Which route table the client uses; `setMode` is the only writer. */
  mode: KanbanMode = 'authenticated';
  /** `true` on the public board (`mode === 'public'`); a field so every existing reader keeps compiling. */
  isAnonymous = false;

  /**
   * Single source of truth for every task the store has loaded, keyed by `String(id)`.
   * `projectTasks` and `kanbanViewTasks` are computed projections over this map (see below) so
   * every existing reader of those two names keeps compiling unchanged.
   */
  tasksById: ObservableMap<string, KanbanTask> = observable.map<string, KanbanTask>();

  deletedProjectTasks: KanbanTask[] = [];
  projectStatuses: KanbanTaskStatus[] = [];
  attachments: Attachment[] = [];
  taskChanges: KanbanTaskChange[] = [];
  currentTask: KanbanTask | null = null;

  // Loaders variables
  private loadingCount = 0;
  isLoading: boolean = false;
  isTitleLoading: boolean = false;
  isDescriptionLoading: boolean = false;
  isStatusLoading: boolean = false;
  isTimeLogLoading: boolean = false;
  attachmentsLoading: boolean = false;
  isScheduledAndPriorityLoading: boolean = false;
  isAssigneeLoading: boolean = false;
  isCustomFieldsLoading: boolean = false;
  isAttachmentDeleting: boolean = false;
  isExportingTimeLogs: boolean = false;

  constructor(apiStore: ApiStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;

    // Compile-time guarantee that this store satisfies the shared TaskStore contract.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _kanbanTaskStoreSatisfiesTaskStore: TaskStore = this;
  }

  //#region Mode (authenticated board vs. public board)
  /**
   * Switches the store, and the API client behind it, between the authenticated and the public
   * board. Everything loaded under the previous mode is dropped: the two boards never share data,
   * and a task or column of one company must not linger in the map while the other is shown.
   */
  setMode = (mode: KanbanMode): void => {
    this.mode = mode;
    this.isAnonymous = mode === 'public';
    this.ApiStore.setKanbanRoutes(routesForMode(mode));
    this.tasksById.clear();
    this.deletedProjectTasks = [];
    this.projectStatuses = [];
    this.attachments = [];
    this.taskChanges = [];
    this.currentTask = null;
    this.projectCustomFields = [];
    this.taskCustomFieldValues = {};
    this.taskTimeMetrics = null;
    this.taskTimeLogs = [];
  };

  /** The board token's project — public mode only (`GET api/OpenProjectApi/project`). */
  getPublicProject = async (): Promise<PublicProject> => {
    if (this.mode !== 'public') {
      throw new Error('getPublicProject is only available in public mode');
    }
    return this.ApiStore.KanbanTaskApiClient.getProject();
  };
  //#endregion

  taskTimeMetrics: TaskTimeMetrics | null = null;
  taskTimeLogs: TaskTimeLog[] = [];

  // Add this to your KanbanTaskStore.ts

  // Custom Fields properties
  projectCustomFields: any[] = [];
  taskCustomFieldValues: Record<string, any[]> = {}; // Map of taskId -> values

  //#region Setters
  setLoading = (loading: boolean): void => {
    if (loading) {
      this.loadingCount++;
    } else {
      this.loadingCount = Math.max(0, this.loadingCount - 1);
    }

    this.isLoading = this.loadingCount > 0;
  }
  //#endregion



  //#region Board/list projections over `tasksById`
  private statusIndex = (task: KanbanTask): number => {
    const idx = this.projectStatuses.findIndex((s) => String(s.id) === String(task.statusId));
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };

  private sortTasks = (tasks: KanbanTask[]): KanbanTask[] => {
    return [...tasks].sort((a, b) => this.statusIndex(a) - this.statusIndex(b) || a.index - b.index);
  };

  /**
   * All loaded tasks, sorted by status column then in-column index.
   *
   * Soft-deleted tasks are left out: a task deleted while it sits in the map (by this client or
   * another one) must disappear from the board and the list. The deleted-tasks view reads
   * `deletedProjectTasks`, which is loaded separately and untouched by this filter.
   */
  get listTasks(): KanbanTask[] {
    return this.sortTasks(Array.from(this.tasksById.values()).filter((task) => !task.isDeleted));
  }

  /** Kept for every existing reader of the list view — same values as `listTasks`. */
  get projectTasks(): KanbanTask[] {
    return this.listTasks;
  }

  /**
   * The board: `listTasks` filtered by `boardRules.isVisibleOnBoard` (final-status tasks drop off
   * seven days after their last modification). This is the only board projection — the
   * `kanban/project` server route is no longer called.
   */
  get kanbanViewTasks(): KanbanTask[] {
    return this.listTasks.filter((task) => isVisibleOnBoard(task, this.projectStatuses));
  }

  getTask = (id: string): KanbanTask | undefined => this.tasksById.get(String(id));

  /**
   * `KanbanTaskRepository.GetTasksByProject` projects the board/list rows **without**
   * `Description` (the card renders `Summary` instead), so writing such a row straight into the
   * map blanks the description of a task the drawer has already fetched in full. Both list loaders
   * drop their old entries before writing the response, so the descriptions they held are passed
   * in as `previous`: a row that does not carry the field keeps the value we already had.
   */
  private setListRow = (task: KanbanTask, previous: Map<string, KanbanTask>): void => {
    const key = String(task.id);
    const kept = previous.get(key)?.description;
    this.tasksById.set(key, task.description === undefined && kept !== undefined ? { ...task, description: kept } : task);
  }

  upsertTask = (task: KanbanTask): void => {
    this.tasksById.set(String(task.id), task);
  };

  patchTask = (taskId: string, patch: Partial<KanbanTask>): void => {
    const key = String(taskId);
    const existing = this.tasksById.get(key);
    if (existing) {
      this.tasksById.set(key, { ...existing, ...patch });
    }
  };

  /** Reinserts `moved` into its (new) status column at `newIndex` and reindexes that column. */
  private reindexSiblings = (moved: KanbanTask, newIndex: number): void => {
    const siblings = Array.from(this.tasksById.values())
      .filter((t) => String(t.statusId) === String(moved.statusId) && String(t.id) !== String(moved.id))
      .sort((a, b) => a.index - b.index);

    const insertAt = Math.max(0, Math.min(newIndex, siblings.length));
    siblings.splice(insertAt, 0, moved);

    siblings.forEach((task, index) => {
      if (task.index !== index) {
        this.patchTask(task.id, { index });
      }
    });
  };

  /** Closes the index gap left in a status column after a task moved out of it. */
  private reindexColumn = (statusId: string, excludeId: string): void => {
    const remaining = Array.from(this.tasksById.values())
      .filter((t) => String(t.statusId) === String(statusId) && String(t.id) !== String(excludeId))
      .sort((a, b) => a.index - b.index);

    remaining.forEach((task, index) => {
      if (task.index !== index) {
        this.patchTask(task.id, { index });
      }
    });
  };
  //#endregion



  //#region Kanban Task Methods
  /** Replaces the map with the full project task list (list view). */
  getProjectTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.KanbanTaskApiClient.getTasks(projectId);
      if (tasks) {
        const previous = new Map(this.tasksById);
        this.tasksById.clear();
        tasks.forEach((task) => this.setListRow(task, previous));
      }
    } finally {
      this.setLoading(false);
    }
  }

  getDeletedProjectTasks = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const tasks = await this.ApiStore.KanbanTaskApiClient.getDeletedTasks(projectId);
      if (tasks) {
        this.deletedProjectTasks = tasks;
      }
    } finally {
      this.setLoading(false);
    }
  }

  getTaskById = async (taskId: string): Promise<KanbanTask | null> => {
    this.setLoading(true);
    try {
      this.currentTask = null;
      const task = await this.ApiStore.KanbanTaskApiClient.getTaskById(taskId);
      this.currentTask = task;
      return task;
    } catch (error) {
      console.error("Failed to load task changes:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  /** Fetches the full task from the server and upserts it into `tasksById` (e.g. for deep links). */
  fetchTask = async (idOrPublicId: string): Promise<KanbanTask | undefined> => {
    const task = await this.ApiStore.KanbanTaskApiClient.getTaskDetails(idOrPublicId);
    if (task) {
      this.upsertTask(task);
    }
    return task ?? undefined;
  }

  getTaskChanges = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const changes = await this.ApiStore.KanbanTaskApiClient.getTaskChanges(taskId);
      this.taskChanges = changes;
    } catch (error) {
      console.error("Failed to load task changes:", error);
    } finally {
      this.setLoading(false);
    }
  }

  createTask = async (task: NewKanbanTask): Promise<KanbanTask | null> => {
    this.setLoading(true);
    try {
      const payload = {
        ...task,
        summary: task?.description ? richTextToPlain(task.description, 100) : ""
      };

      const result = await this.ApiStore.KanbanTaskApiClient.createTask(payload);
      if (result) {
        const fullTask = await this.ApiStore.KanbanTaskApiClient.getTaskDetails(result.id.toString());
        const taskToAdd = fullTask ? fullTask : result;

        this.upsertTask(taskToAdd);

        return taskToAdd;
      }
      return null;
    } catch (error) {
      console.error("Error creating task:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  };


  updateTask = async (taskId: string, task: KanbanTask): Promise<void> => {
    this.setLoading(true);
    try {
      const originalTask = this.tasksById.get(String(taskId));
      const assigneeChanged = originalTask &&
        String(originalTask.assigneeId) !== String(task.assigneeId);

      const result = await this.ApiStore.KanbanTaskApiClient.updateTask(taskId, task);

      if (result) {
        if (assigneeChanged && task.assigneeId) {
          const fullTaskDetails = await this.ApiStore.KanbanTaskApiClient.getTaskDetails(taskId);
          this.upsertTask(fullTaskDetails ?? result);
        } else {
          this.upsertTask(result);
        }
      }
    } catch (error) {
      console.error("Failed to update task:", error);
    } finally {
      this.setLoading(false);
    }
  }

  updateTaskOrder = async (taskId: string, newIndex: number): Promise<void> => {
    this.setLoading(true);
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskOrder(taskId, newIndex);
      if (updatedTask) {
        // Apply the server's returned task and reindex its column locally instead of
        // refetching both the list and kanban views.
        this.upsertTask(updatedTask);
        this.reindexSiblings(updatedTask, typeof updatedTask.index === "number" ? updatedTask.index : newIndex);
      }
    } finally {
      this.setLoading(false);
    }
  }

  restoreTask = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const restored = await this.ApiStore.KanbanTaskApiClient.restoreTask(taskId);

      if (restored) {
        this.deletedProjectTasks = this.deletedProjectTasks.filter(t => String(t.id) !== String(taskId));

        // `listTasks` hides `isDeleted` tasks, so the flag has to be cleared or the restored task
        // would go back into the map invisible.
        this.upsertTask({ ...restored, isDeleted: false });

        if (this.currentTask?.id === restored.id) {
          this.currentTask = restored;
        }
      }
    } catch (error) {
      console.error("Failed to restore task:", error);
    } finally {
      this.setLoading(false);
    }
  }

  async bulkRestore(taskIds: string[]) {
    const result = await this.ApiStore.KanbanTaskApiClient.bulkRestore(taskIds);

    const restored = this.deletedProjectTasks.filter(t =>
      taskIds.includes(t.id.toString())
    );

    // These come straight out of the deleted list, so they still carry `isDeleted` — see restoreTask.
    restored.forEach((task) => this.upsertTask({ ...task, isDeleted: false }));
    this.deletedProjectTasks = this.deletedProjectTasks.filter(
      t => !taskIds.includes(t.id.toString())
    );

    return result;
  }

  deleteTask = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteTask(taskId);
      if (result) {
        this.tasksById.delete(String(taskId));
      }
    } finally {
      this.setLoading(false);
    }
  }

  async bulkSoftDelete(taskIds: string[]) {
    const result = await this.ApiStore.KanbanTaskApiClient.bulkSoftDelete(taskIds);

    taskIds.forEach((id) => this.tasksById.delete(String(id)));

    return result;
  }

  deleteTaskPermanent = async (taskId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteTaskPermanent(taskId);
      if (result) {
        this.deletedProjectTasks = this.deletedProjectTasks.filter(task => task.id !== taskId);
      }
    } finally {
      this.setLoading(false);
    }
  }

  async bulkPermanentDelete(taskIds: string[]) {
    const result = await this.ApiStore.KanbanTaskApiClient.bulkPermanentDelete(taskIds);

    this.deletedProjectTasks = this.deletedProjectTasks.filter(t => !taskIds.includes(t.id.toString()));

    return result;
  }
  //#endregion



  //#region Task Movement Method
  async moveTask(taskId: string, toStatusId: string, toIndex: number): Promise<void> {
    this.setLoading(true);
    const key = String(taskId);
    const previous = this.tasksById.get(key);
    try {
      // The server's `/move` endpoint returns `200 OK` with no body, so there is no
      // "returned task" to apply here — the move is instead reflected optimistically from the
      // task already in the map, with both the source and destination columns reindexed locally.
      await this.ApiStore.KanbanTaskApiClient.moveTask(taskId, toStatusId, toIndex);
      if (previous) {
        const statusChanged = String(previous.statusId) !== String(toStatusId);
        const moved: KanbanTask = { ...previous, statusId: toStatusId, index: toIndex };
        this.upsertTask(moved);
        this.reindexSiblings(moved, toIndex);
        if (statusChanged) {
          this.reindexColumn(previous.statusId, key);
        }
      }
    } finally {
      this.setLoading(false);
    }
  }
  //#endregion



  //#region Custom Fields Methods
  getProjectCustomFields = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const fields = await this.ApiStore.KanbanTaskApiClient.getProjectCustomFields(projectId);
      if (fields) {
        this.projectCustomFields = fields;
      }
    } catch (error) {
      console.error("Failed to load project custom fields:", error);
    } finally {
      this.setLoading(false);
    }
  }

  getTaskCustomFieldValues = async (taskId: string): Promise<void> => {
    if (!taskId) return;

    // this.setLoading(true); //todo not use main Load its blocking all page
    try {
      const values = await this.ApiStore.KanbanTaskApiClient.getTaskCustomFieldValues(taskId);
      if (values) {
        this.taskCustomFieldValues[taskId] = values;
      }
    } catch (error) {
      console.error("Failed to load task custom field values:", error);
    } finally {
      // this.setLoading(false);
    }
  }

  setTaskCustomFieldValue = async (taskId: string, fieldId: string, value: string, userId?: string): Promise<boolean> => {
    this.isCustomFieldsLoading = true;
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.setTaskCustomFieldValue(taskId, fieldId, value, userId);
      if (result) {
        // Update local state
        await this.getTaskCustomFieldValues(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to set custom field value:", error);
      return false;
    } finally {
      this.isCustomFieldsLoading = false;
    }
  }

  updateTaskCustomFieldValues = async (taskId: string, fieldValues: Record<string, string>): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.updateTaskCustomFieldValues(taskId, fieldValues);
      if (result) {
        // Update local state
        await this.getTaskCustomFieldValues(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to update task custom field values:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  createCustomField = async (field: any): Promise<any> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.createCustomField(field);
      if (result) {
        // Update local state
        this.projectCustomFields.push(result);
        // Sort by display order
        this.projectCustomFields.sort((a, b) => a.displayOrder - b.displayOrder);
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create custom field:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  updateCustomField = async (fieldId: string, field: any): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.updateCustomField(fieldId, field);

      const index = this.projectCustomFields.findIndex(f => f.id.toString() === fieldId.toString());
      if (index !== -1) {
        const updatedField = {
          ...this.projectCustomFields[index],
          ...field,
          ...(result || {})
        };

        this.projectCustomFields = [
          ...this.projectCustomFields.slice(0, index),
          updatedField,
          ...this.projectCustomFields.slice(index + 1)
        ];
      }

      return true;
    } catch (error) {
      console.error("Failed to update custom field:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  deleteCustomField = async (fieldId: string): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteCustomField(fieldId);
      if (result) {
        // Update local state
        this.projectCustomFields = this.projectCustomFields.filter(f => f.id.toString() !== fieldId.toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to delete custom field:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  // Helper methods for custom fields
  getFieldValueForTask = (taskId: string, fieldId: string): any => {
    const values = this.taskCustomFieldValues[taskId] || [];
    const valueObj = values.find(v => v.customFieldId.toString() === fieldId.toString());
    return valueObj ? valueObj.value : null;
  }

  getFieldDefinition = (fieldId: string): any => {
    return this.projectCustomFields.find(f => f.id.toString() === fieldId.toString());
  }
  //#endregion



  //#region Time Tracking Methods
  getTaskTimeMetrics = async (taskId: string): Promise<TaskTimeMetrics | null> => {
    // this.setLoading(true);  //todo not use main loading its block page
    try {
      const metrics = await this.ApiStore.KanbanTaskApiClient.getTaskTimeMetrics(taskId);
      this.taskTimeMetrics = metrics;
      return metrics;
    } catch (error) {
      console.error("Failed to load task time metrics:", error);
      return null;
    } finally {
      // this.setLoading(false);
    }
  }

  updateEstimatedTime = async (taskId: string, estimatedTime: string): Promise<boolean> => {
    this.setLoading(true);
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.updateEstimatedTime(taskId, estimatedTime);

      if (result) {
        this.taskTimeMetrics = result;
        this.patchTask(taskId, { estimatedTime });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to update estimated time:", error);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  logTimeForTask = async (
    taskId: string,
    time: string,
    description: string,
    userId?: string,
    endDate?: string
  ): Promise<boolean> => {
    //
    this.isTimeLogLoading = true;
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.logTimeForTask(
        taskId, time, description, userId, endDate
      );

      if (result) {
        // Update metrics and logs with the returned data
        if (result.metrics) {
          this.taskTimeMetrics = result.metrics;
        } else {
          // If metrics weren't returned, fetch them
          await this.getTaskTimeMetrics(taskId);
        }

        // Refresh the time logs
        await this.getTaskTimeLogs(taskId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to log time for task:", error);
      return false;
    } finally {
      this.isTimeLogLoading = false;
    }
  }

  getTaskTimeLogs = async (taskId: string): Promise<TaskTimeLog[]> => {
    this.isTimeLogLoading = true;
    try {
      const logs = await this.ApiStore.KanbanTaskApiClient.getTaskTimeLogs(taskId);
      this.taskTimeLogs = logs;
      return logs;
    } catch (error) {
      console.error("Failed to load task time logs:", error);
      return [];
    } finally {
      this.isTimeLogLoading = false;
    }
  }
  //#endregion



  //#region Attachment Methods
  getTaskAttachments = async (taskId: string): Promise<Attachment[]> => {
    this.attachmentsLoading = true;
    try {
      const att = await this.ApiStore.KanbanTaskApiClient.getTaskAttachments(taskId);
      runInAction(() => {
        this.attachments = Array.isArray(att) ? att : [];
      });
      return this.attachments;
    } catch (error) {
      console.error('Store: Error fetching attachments:', error);
      runInAction(() => {
        this.attachments = [];
      });
      return [];
    } finally {
      runInAction(() => {
        this.attachmentsLoading = false;
      });
    }
  };

  uploadTaskAttachment = async (taskId: string, file: File, confirmation: boolean = false): Promise<Attachment | null> => {
    this.setLoading(true);
    try {
      // Verify the task exists in our local cache before hitting the network.
      const task = this.tasksById.get(String(taskId));
      if (!task) {
        throw new Error('Task not found');
      }

      const result = await this.ApiStore.KanbanTaskApiClient.uploadTaskAttachment(taskId, file, confirmation, task.projectId);

      if (result) {
        runInAction(() => {
          this.attachments = [result, ...this.attachments.filter((a) => a.publicId !== result.publicId)];
        });
        return result;
      }
      return null;
    } catch (error) {
      console.error("Store: Error uploading attachment:", error);
      throw error; // Rethrow to handle in component
    } finally {
      this.setLoading(false);
    }
  };

  /** Uploads an image referenced from the task's rich-text description/comments. */
  uploadTaskImage = async (taskId: string, file: File): Promise<string> => {
    return this.ApiStore.KanbanTaskApiClient.uploadTaskImage(taskId, file);
  };

  deleteAttachment = async (publicId: string, taskId: string): Promise<boolean> => {
    this.isAttachmentDeleting = true;
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteAttachment(taskId, publicId);

      if (result) {
        // Update attachments in the store
        this.attachments = this.attachments.filter((att) => att.publicId !== publicId);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Store: Error deleting attachment:", error);
      return false;
    } finally {
      this.isAttachmentDeleting = false;
    }
  };
  //#endregion



  //#region Comments
  getTaskComments = async (taskId: string): Promise<KanbanTaskComment[]> => {
    return this.ApiStore.KanbanTaskApiClient.getTaskComments(taskId);
  };

  createComment = async (comment: NewKanbanTaskComment): Promise<KanbanTaskComment | null> => {
    return this.ApiStore.KanbanTaskApiClient.createComment(comment);
  };
  //#endregion



  //#region Task Detail Loading
  /** Loads the task row, changes, attachments, time and custom fields — what the drawer loads on open. Comments are fetched by TaskComments itself. */
  loadTaskDetails = async (taskId: string): Promise<void> => {
    // Never show the previous task's files while this task's list is still loading.
    this.attachments = [];
    this.attachmentsLoading = true;
    await Promise.allSettled([
      // The board/list endpoints project the task without its description; fetch the full row.
      this.fetchTask(taskId),
      this.getTaskChanges(taskId),
      this.getTaskAttachments(taskId),
      this.getTaskTimeMetrics(taskId),
      this.getTaskTimeLogs(taskId),
      this.getTaskCustomFieldValues(taskId),
    ]);
  };
  //#endregion



  //#region Methods to update specific task fields
  /**
   * `projectTasks` and `kanbanViewTasks` are computed getters over `tasksById` (see above), so a
   * single `upsertTask`/`patchTask` write into the map is seen by both the list and the board —
   * there is no separate array to keep in sync here, unlike in the pre-refactor store.
   */
  updateTaskStatus = async (taskId: string, statusId: string): Promise<void> => {
    const previous = this.tasksById.get(String(taskId));
    if (previous) this.patchTask(taskId, { statusId });

    this.isStatusLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskStatus(taskId, statusId);
      if (updatedTask) this.upsertTask(updatedTask);
    } catch (error) {
      if (previous) this.tasksById.set(String(taskId), previous);
      throw error;
    } finally {
      this.isStatusLoading = false;
    }
  }

  updateTaskTitle = async (taskId: string, title: string): Promise<void> => {
    const previous = this.tasksById.get(String(taskId));
    this.patchTask(taskId, { title });

    this.isTitleLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskTitle(taskId, title);
      if (updatedTask) this.upsertTask(updatedTask);
    } catch (error) {
      if (previous) this.tasksById.set(String(taskId), previous);
      else this.tasksById.delete(String(taskId));
      throw error;
    } finally {
      this.isTitleLoading = false;
    }
  }

  /** Optimistic single-PATCH save: the server now computes and stores the summary itself (Task 2). */
  updateTaskDescription = async (taskId: string, html: string): Promise<void> => {
    const previous = this.tasksById.get(String(taskId));
    const summary = richTextToPlain(html, 100);
    this.patchTask(taskId, { description: html, summary });

    this.isDescriptionLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskDescription(taskId, html);
      if (updatedTask) this.upsertTask(updatedTask);
    } catch (error) {
      if (previous) this.tasksById.set(String(taskId), previous);
      else this.tasksById.delete(String(taskId));
      if (isInlineImagesNotAllowedError(error)) {
        message.error(i18n.t('editorInlineImagesNotAllowed'));
      }
      throw error;
    } finally {
      this.isDescriptionLoading = false;
    }
  };

  updateTaskScheduledAndPriority = async (taskId: string, scheduledAndPriority: ScheduledAndPriorityUpdate): Promise<void> => {
    const previous = this.tasksById.get(String(taskId));
    if (previous) {
      this.patchTask(taskId, {
        startDate: scheduledAndPriority.startDate ? dayjs(scheduledAndPriority.startDate) : undefined,
        dueDate: scheduledAndPriority.dueDate ? dayjs(scheduledAndPriority.dueDate) : undefined,
        priority: scheduledAndPriority.priority,
      });
    }

    this.isScheduledAndPriorityLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskScheduledAndPriority(taskId, scheduledAndPriority);
      if (updatedTask) this.upsertTask(updatedTask);
    } catch (error) {
      if (previous) this.tasksById.set(String(taskId), previous);
      throw error;
    } finally {
      this.isScheduledAndPriorityLoading = false;
    }
  }

  updateTaskAssignee = async (taskId: string, assigneeId?: string): Promise<void> => {
    const previous = this.tasksById.get(String(taskId));
    if (previous) this.patchTask(taskId, { assigneeId });

    this.isAssigneeLoading = true;
    try {
      const updatedTask = await this.ApiStore.KanbanTaskApiClient.updateTaskAssignee(taskId, assigneeId);
      if (updatedTask) this.upsertTask(updatedTask);
    } catch (error) {
      if (previous) this.tasksById.set(String(taskId), previous);
      throw error;
    } finally {
      this.isAssigneeLoading = false;
    }
  }
  //#endregion



  //#region Task Status Methods
  /** Rethrows on a failed request: the public page reads a 401 here as an expired link. */
  getProjectStatuses = async (projectId: string): Promise<void> => {
    this.setLoading(true);
    try {
      const statuses = await this.ApiStore.KanbanTaskApiClient.getProjectStatuses(projectId);
      this.projectStatuses = [...statuses].sort((a, b) => a.index - b.index);
    } catch (error) {
      console.error("Failed to load project statuses:", error);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  createStatus = async (name: string, projectId: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> => {
    this.setLoading(true);
    try {
      // Calculate next index
      const nextIndex = this.projectStatuses.length;

      const result = await this.ApiStore.KanbanTaskApiClient.createStatus(name, projectId, nextIndex, isFinalStatus);
      if (result) {
        this.projectStatuses.push(result);
        this.projectStatuses = this.projectStatuses.sort((a, b) => a.index - b.index);
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to create status:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  updateStatus = async (statusId: string, name: string, isFinalStatus: boolean): Promise<KanbanTaskStatus | null> => {
    this.setLoading(true);
    try {
      const status = this.projectStatuses.find(s => s.id === statusId);
      if (!status) {
        throw new Error(`Status with ID ${statusId} not found`);
      }

      const updatedStatus = { ...status, name, isFinalStatus };
      const result = await this.ApiStore.KanbanTaskApiClient.updateStatus(statusId, updatedStatus);

      if (result) {
        this.projectStatuses = this.projectStatuses.map(s =>
          s.id === statusId ? result : s
        );
        return result;
      }
      return null;
    } catch (error) {
      console.error("Failed to update status:", error);
      return null;
    } finally {
      this.setLoading(false);
    }
  }

  updateStatusOrder = async (statusId: string, newIndex: number): Promise<void> => {
    this.setLoading(true);
    try {
      await this.ApiStore.KanbanTaskApiClient.updateStatusOrder(statusId, newIndex);
      // Refresh the statuses from the server to get the updated order
      const projectId = this.projectStatuses.find(s => s.id === statusId)?.projectId;
      if (projectId) {
        await this.getProjectStatuses(projectId.toString());
      }
    } catch (error) {
      console.error("Failed to update status order:", error);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Resolves true when the column is gone. Throws StatusNotEmptyError (taskCount) when the server
   * refuses a non-empty column without a target — the board then asks where to move the tasks.
   */
  deleteStatus = async (statusId: number, moveTasksTo?: number): Promise<boolean> => {
    this.setLoading(true);
    // The board works with numeric ids, the client and KanbanPaths with string ids (Task 9).
    const deletedId = String(statusId);
    const projectId = this.projectStatuses.find((s) => String(s.id) === deletedId)?.projectId;
    try {
      const result = await this.ApiStore.KanbanTaskApiClient.deleteStatus(deletedId, moveTasksTo);
      if (!result) {
        return false;
      }
      runInAction(() => {
        this.projectStatuses = this.projectStatuses.filter((s) => String(s.id) !== deletedId);
        const affected = Array.from(this.tasksById.values()).filter((t) => String(t.statusId) === deletedId);
        if (moveTasksTo != null) {
          const targetId = String(moveTasksTo);
          affected.forEach((t) => this.patchTask(String(t.id), { statusId: targetId }));
        } else {
          affected.forEach((t) => this.tasksById.delete(String(t.id)));
        }
      });
      if (moveTasksTo != null && projectId) {
        // The server appended the moved tasks after the target's last index; re-read so the order matches.
        await this.getProjectTasks(String(projectId)).catch((e) => console.warn('Board refresh after move failed:', e));
      }
      return true;
    } catch (error) {
      if (isStatusNotEmptyError(error)) {
        throw error;
      }
      console.error('Failed to delete status:', error);
      return false;
    } finally {
      this.setLoading(false);
    }
  };
  //#endregion



  //#region Methods For Export Files
  extractFilename(disposition?: string): string | null {
    if (!disposition) return null;
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
    if (star?.[1]) return decodeURIComponent(star[1].replace(/^["']|["']$/g, ''));
    const plain = /filename=(?:UTF-8'')?([^;]+)/i.exec(disposition);
    if (plain?.[1]) return plain[1].trim().replace(/^["']|["']$/g, '');
    return null;
  }

  downloadBlob(potentialBlob: any, filename = 'export.csv') {
    const blob = this.toCsvBlob(potentialBlob, 'text/csv');

    if (!blob || blob.size === 0) {
      console.warn('Empty blob - nothing to load');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  exportTimeLogsAndDownload = async (params: ExportTimeLogsParams): Promise<boolean> => {
    runInAction(() => { this.isExportingTimeLogs = true; });
    try {
      const res = await this.ApiStore.KanbanTaskApiClient.exportProjectTimeLogs(params);

      if (res instanceof Blob) {
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        this.downloadBlob(res, fallback);
        return true;
      }

      if (res && typeof res === 'object' && 'blob' in (res as any)) {
        const r: any = res;
        const cd = r.headers?.['content-disposition'];
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        const filename = this.extractFilename(cd) || fallback;
        this.downloadBlob(r.blob, filename);
        return true;
      }

      if (typeof res === 'string' || res instanceof ArrayBuffer || ArrayBuffer.isView(res)) {
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        this.downloadBlob(res, fallback);
        return true;
      }
      if (res && typeof res === 'object' && 'data' in res) {
        const r: any = res;
        const cd = r.headers?.['content-disposition'];
        const fallback = `project-time-logs_${dayjs().format('YYYY-MM-DD')}.csv`;
        const filename = this.extractFilename(cd) || fallback;
        this.downloadBlob(r.data, filename);
        return true;
      }

      console.error('exportTimeLogsAndDownload: unsupported response shape', res);
      return false;
    } catch (e) {
      console.error('exportTimeLogsAndDownload error:', e);
      return false;
    } finally {
      runInAction(() => { this.isExportingTimeLogs = false; });
    }
  };

  toCsvBlob(content: unknown, mime = "text/csv"): Blob {
    if (content instanceof Blob) return content;

    if (content instanceof ArrayBuffer) return new Blob([content], { type: mime });

    if (ArrayBuffer.isView(content)) {
      const view = content as ArrayBufferView;
      const copy = new Uint8Array(view.buffer, view.byteOffset, view.byteLength).slice();
      return new Blob([copy], { type: mime });
    }

    if (content && typeof content === "object") {
      const anyObj = content as any;
      if (typeof anyObj.byteLength === "number" && anyObj.byteLength >= 0) {
        const copy = new Uint8Array(new Uint8Array(anyObj as ArrayBufferLike)).slice();
        return new Blob([copy], { type: mime });
      }
      if (Array.isArray(content)) {
        const u8 = Uint8Array.from(content as number[]);
        return new Blob([u8], { type: mime });
      }
    }
    if (typeof content === "string") return new Blob([content], { type: mime });
    return new Blob([String(content ?? "")], { type: mime });
  }
  //#endregion
}

export default KanbanTaskStore;
